import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { useDispatch } from "react-redux";

import { authService } from "@/services/auth";
import { getApiFieldErrors } from "@/services/api";
import {
  getOrganizationSettingsErrorMessage,
  organizationSettingsService,
  type OrganizationSettings,
} from "@/services/organizationSettings";
import { ORGANIZATION_SETTINGS_QUERY_KEY } from "@/hooks/queries";
import { setUser } from "@/store/slices/authSlice";
import type { AppDispatch } from "@/store/store";
import TrialLocked from "@/components/trial/TrialLocked";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface BrandingSectionProps {
  settings: OrganizationSettings | undefined;
  loading: boolean;
}

const BRAND_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const BrandingSection = ({ settings, loading }: BrandingSectionProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setName(settings.name);
    setBrandColor(settings.brand_color);
    setContactEmail(settings.contact_email ?? "");
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () =>
      organizationSettingsService.update({
        name: name.trim(),
        brand_color: brandColor,
        contact_email: contactEmail.trim(),
      }),
    onMutate: () => {
      setError(null);
      setFieldErrors({});
      setSaved(false);
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(ORGANIZATION_SETTINGS_QUERY_KEY, data);
      setName(data.name);
      setBrandColor(data.brand_color);
      setContactEmail(data.contact_email);
      try {
        dispatch(setUser(await authService.getCurrentUser()));
      } catch {
        // Branding already saved; account chrome refreshes on the next load.
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
    onError: (err) => {
      const fields = getApiFieldErrors(err);
      if (fields.name || fields.brand_color || fields.contact_email) {
        setFieldErrors(fields);
      } else {
        setError(getOrganizationSettingsErrorMessage(err));
      }
    },
  });

  const nameLocked = settings?.trial_locked_fields.includes("name") ?? false;
  const colorLocked = settings?.trial_locked_fields.includes("brand_color") ?? false;
  const emailLocked = settings?.trial_locked_fields.includes("contact_email") ?? false;
  const dirty =
    name.trim() !== (settings?.name ?? "") ||
    brandColor !== (settings?.brand_color ?? "") ||
    contactEmail.trim() !== (settings?.contact_email ?? "");
  const valid = name.trim().length > 0 && BRAND_COLOR_PATTERN.test(brandColor);

  const save = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Organization name cannot be empty.";
    if (!BRAND_COLOR_PATTERN.test(brandColor)) {
      errors.brand_color = "Enter a color in #RRGGBB format.";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    mutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>
          Set the organization name, color and contact email merchants see in flows, emails and
          documents.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="branding-name">Organization name</Label>
          <TrialLocked locked={nameLocked} lock="orgName">
            <Input
              id="branding-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={loading || mutation.isPending}
              aria-invalid={!!fieldErrors.name}
            />
          </TrialLocked>
          {fieldErrors.name && <p className="text-sm text-destructive">{fieldErrors.name}</p>}
          <p className="text-xs text-muted-foreground">
            Must be unique across the platform. Admin profile names update when this changes.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="branding-color">Brand color</Label>
          <TrialLocked locked={colorLocked}>
            <div className="flex items-center gap-3">
              <Input
                id="branding-color-picker"
                type="color"
                value={BRAND_COLOR_PATTERN.test(brandColor) ? brandColor : "#5b6ef5"}
                onChange={(event) => setBrandColor(event.target.value)}
                disabled={loading || mutation.isPending}
                className="w-14 cursor-pointer p-1"
                aria-label="Choose brand color"
              />
              <Input
                id="branding-color"
                value={brandColor}
                onChange={(event) => setBrandColor(event.target.value)}
                disabled={loading || mutation.isPending}
                maxLength={7}
                placeholder="#5b6ef5"
                aria-invalid={!!fieldErrors.brand_color}
                className="max-w-40 font-mono"
              />
            </div>
          </TrialLocked>
          {fieldErrors.brand_color && (
            <p className="text-sm text-destructive">{fieldErrors.brand_color}</p>
          )}
          <p className="text-xs text-muted-foreground">Use a six-digit hexadecimal color.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="branding-email">Funder contact email</Label>
          <TrialLocked locked={emailLocked}>
            <Input
              id="branding-email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
              disabled={loading || mutation.isPending}
              type="email"
              placeholder="funding@example.com"
              aria-invalid={!!fieldErrors.contact_email}
            />
          </TrialLocked>
          {fieldErrors.contact_email && (
            <p className="text-sm text-destructive">{fieldErrors.contact_email}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Appears as the funder&apos;s contact email on ACH addendums.
          </p>
        </div>

        <TrialLocked locked={nameLocked || colorLocked || emailLocked} inline>
          <Button
            type="button"
            onClick={save}
            disabled={loading || mutation.isPending || !dirty || !valid}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {mutation.isPending ? "Saving..." : "Save branding"}
          </Button>
        </TrialLocked>
        {saved && <span className="ml-2 text-xs text-muted-foreground">Saved.</span>}
      </CardContent>
    </Card>
  );
};

export default BrandingSection;
